import { useModal } from '@ebay/nice-modal-react'
import { styled } from '@linaria/react'
import { useNavigate } from '@tanstack/router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  WITHDRAW_CHANNELS_EN,
  WITHDRAW_CHANNELS_ES,
} from '../../constants/wallet-management'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { WalletDetails } from '../../types/wallet-management'
import type { BankCodeObj } from '../../types/wallet-management'
import { api } from '../../utils/api-client'
import classNames from '../../utils/classNames'
import MessageModal from '../common/message-modal'
import { type Item } from '../common/scroll-selector'
import { TailSpin } from '../loaders'
import AboutWithdraw from '../withdraw-form/about-withdraw'
import ActionButtons from '../withdraw-form/action-buttons'
import AddBank from '../withdraw-form/add-bank'
// import AddUsdt from '../withdraw-form/add-usdt'
import Balance from '../withdraw-form/balance'
import SelectWallet from '../withdraw-form/select-wallet'
import AmountInput from './amount-input'
import ConfirmButton from './confirm-button'
import Platform from './platform'
import WithdrawHistory from './withdraw-history'

const Grid = styled.div`
  padding: 12px;

  @media (min-width: 1200px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding: 0px 48px 48px 48px;
    gap: 20px;
`

const ChannelsContainer = styled.div`
  width: 100%;
`

const FormContainer = styled.div`
  width: 100%;
  margin-bottom: 30px;

  @media (min-width: 1200px) {
    padding-right: 12px;
    margin-bottom: unset;
    height: 400px;
    overflow-y: auto;
  }

  /* width */
  &::-webkit-scrollbar {
    width: 18px;
  }

  /* Track */
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    border: 5px solid transparent;
    border-radius: 9px;
    background-clip: content-box;
  }

  .theme-dark &::-webkit-scrollbar-thumb {
    background-color: #ccc;
  }

  .theme-light &::-webkit-scrollbar-thumb {
    background-color: #ccc;
  }
`

const BankContainer = styled.div`
  width: 100%;
  padding-bottom: 30px;

  @media (min-width: 1200px) {
    padding-right: 12px;
    max-height: 500px;
    overflow-y: auto;
    width: 500px;
    margin: 0 auto;
  }

  /* width */
  &::-webkit-scrollbar {
    width: 18px;
  }

  /* Track */
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    border: 5px solid transparent;
    border-radius: 9px;
    background-clip: content-box;
  }

  .theme-dark &::-webkit-scrollbar-thumb {
    background-color: #ccc;
  }

  .theme-light &::-webkit-scrollbar-thumb {
    background-color: #ccc;
  }
`

type WithdrawProps = {
  closeWalletManagementModal: () => void
  showWalletManagementModal: (page?: number, tab?: number) => void
  walletManagementDefaultTab?: {
    tab?: number
    page?: number
  }
}

const Withdraw = ({ closeWalletManagementModal }: WithdrawProps) => {
  const navigate = useNavigate()
  const isVerified = useBoundStore(state => state.user.is_verified)
  const { t, i18n } = useTranslation(['common', 'walletManagement'])
  const { language } = i18n
  const messageModal = useModal(MessageModal)
  const channels =
    language === 'es' ? WITHDRAW_CHANNELS_ES : WITHDRAW_CHANNELS_EN
  const [selectedChannel, setSelectedChannel] = useState(0)
  const [amount, setAmount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState(false)
  const [walletList, setWalletList] = useState<WalletDetails[]>([])
  const [selectedWallet, setSelectedWallet] = useState<WalletDetails | null>(
    null,
  )
  const [activeTab, setActiveTab] = useState<string>('withdraw')
  const [bankTab, setBankTab] = useState<'add' | 'list'>('add')
  const [bankList, setBankList] = useState<Item[]>([])

  const changeTab = (tab: string) => {
    setActiveTab(tab)

    if (bankTab === 'list') setBankTab('add')
  }

  const withdrawHandler = async () => {
    if (!selectedWallet) return

    if (+isVerified !== 1) {
      messageModal.show({
        message: t('walletManagement:please-do-kyc'),
        onClick: () => {
          closeWalletManagementModal()
          navigate({ to: '/profile/kyc' })
        },
      })
      return
    }

    const reqBody = {
      submit_type: 'debit',
      debit_bank: selectedWallet.id,
      amount: amount,
    }

    setIsLoading(true)
    const response = await api.post<ApiResponse<string>>('/center.php', reqBody)
    const { status, info } = response.data
    if (status === 1) {
      messageModal.show({
        title: t('successful', { ns: 'common' }),
        message: t('processing', { ns: 'walletManagement' }),
      })
    } else {
      messageModal.show({
        title: '',
        message: info,
      })
    }
    setIsLoading(false)
  }

  const getWalletList = async () => {
    const response = await api.post<ApiResponse<WalletDetails[]>>(
      '/ajax_data.php',
      { type: 'bank_list' },
    )
    const { status, info } = response.data
    if (status === 1) {
      if (selectedChannel === 0)
        setWalletList(
          info.filter(
            item =>
              item.bank_type !== 'USDTERC20' && item.bank_type !== 'USDTTRC20',
          ),
        )
      else if (selectedChannel === 1)
        setWalletList(
          info.filter(
            item =>
              item.bank_type === 'USDTERC20' || item.bank_type === 'USDTTRC20',
          ),
        )
    }
  }

  const fetchBankList = async () => {
    try {
      setIsLoading(true)

      const response = await api.post<ApiResponse<BankCodeObj[]>>(
        '/payment.php',
        {
          type: 'withdraw_bank_list',
        },
      )

      const { status, info } = response.data
      if (status === 1) {
        setBankList(
          info.map(bank => ({
            label: `${bank.bank_name} - ${bank.bank_code}`,
            id: bank.bank_code,
          })),
        )
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const renderButton = () => {
    if (isLoading)
      return (
        <div className="flx-ctr" style={{ height: '60px' }}>
          <TailSpin width={39} height={39} color="#fff" />
        </div>
      )

    return (
      <ConfirmButton
        onClick={withdrawHandler}
        disabled={amount <= 0 || selectedWallet === null}>
        {t('withdraw', { ns: 'walletManagement' })}
      </ConfirmButton>
    )
  }

  const renderRightContent = () => {
    return (
      <div className="overflow-hidden">
        <SelectWallet
          type={selectedChannel === 0 ? 'bank' : 'usdt'}
          value={selectedWallet ? selectedWallet.debit_bank : ''}
          walletList={walletList}
          setSelectedWallet={setSelectedWallet}
        />
        <ActionButtons
          type={selectedChannel === 0 ? 'bank' : 'usdt'}
          onAdd={() => {
            setActiveTab(selectedChannel === 0 ? 'bank' : 'usdt')
            fetchBankList()
          }}
        />
        <AmountInput
          type={selectedChannel === 0 ? 'bank' : 'usdt'}
          amount={amount}
          setAmount={setAmount}
        />
        {renderButton()}
        <AboutWithdraw />
      </div>
    )
  }

  useEffect(() => {
    getWalletList()
  }, [selectedChannel])

  return (
    <div>
      <div className="withdraw-form w-full max-h-[calc(100vh_-_96px)] overflow-y-auto pt-6 pb-0 px-3 xl:max-h-[calc(100vh_-_100px)] xl:p-[48px_48px_0px]">
        <div className="flex flex-wrap gap-3 mb-4 xl:gap-4 xl:mb-6">
          <button
            className={classNames(
              'text-sm leading-5 font-medium uppercase transition-all duration-[0.2s] ease-[ease-in-out] xl:text-lg',
              activeTab === 'withdraw'
                ? 'text-highlight border-solid border-t-0 border-r-0 border-l-0 border-b-2 border-highlight'
                : 'text-txt',
            )}
            onClick={() => changeTab('withdraw')}
            aria-label={t('walletManagement:withdraw')}>
            {t('walletManagement:withdraw').toUpperCase()}
          </button>
          <button
            className={classNames(
              'text-sm leading-5 font-medium uppercase transition-all duration-[0.2s] ease-[ease-in-out] xl:text-lg',
              activeTab === 'bank'
                ? 'text-highlight border-solid border-t-0 border-r-0 border-l-0 border-b-2 border-highlight'
                : 'text-txt',
            )}
            onClick={() => {
              changeTab('bank')
              fetchBankList()
            }}
            aria-label={t('walletManagement:bank')}>
            {t('walletManagement:bank')}
          </button>
          {/* <button
              className={classNames(
                'text-sm leading-5 font-medium uppercase transition-all duration-[0.2s] ease-[ease-in-out] xl:text-lg',
                activeTab === 'usdt'
                  ? 'text-highlight border-solid border-t-0 border-r-0 border-l-0 border-b-2 border-highlight'
                  : 'text-txt',
              )}
              onClick={() => changeTab('usdt')}
              aria-label="USDT">
              USDT
            </button> */}
        </div>
      </div>

      {activeTab === 'withdraw' && (
        <Grid>
          <div>
            <div className="mb-[30px] xl:mb-[unset]">
              <span className="text-txt border-b-highlight border-b-2">
                {t('money', { ns: 'common' })}
              </span>
              <div className="mt-3">
                <Balance />
              </div>
            </div>
            <FormContainer>
              <div className="mb-5">
                <span className="text-txt border-b-highlight border-b-2">
                  {t('withdrawal-type', { ns: 'walletManagement' })}
                </span>
              </div>

              {channels.map((item, index) => (
                <Platform
                  key={index}
                  icon={item.icon}
                  label={item.label}
                  active={index === selectedChannel}
                  onClick={() => setSelectedChannel(index)}
                  from="withdraw"
                />
              ))}
              <div className="mt-5">{renderRightContent()}</div>
            </FormContainer>
          </div>
          <ChannelsContainer>
            <WithdrawHistory
              closeWalletManagementModal={closeWalletManagementModal}
              from="withdraw"
            />
          </ChannelsContainer>
        </Grid>
      )}
      {activeTab === 'bank' && (
        <BankContainer>
          <AddBank
            bankList={bankList}
            getWalletList={getWalletList}
            onSuccess={() => changeTab('withdraw')}
          />
        </BankContainer>
      )}
      {/* {activeTab === 'usdt' && (
        <BankContainer>
          <AddUsdt
            getWalletList={getWalletList}
            isVisible={true}
            setIsVisible={isVisible =>
              showWalletManagementModal(isVisible ? 1 : 0)
            }
          />
        </BankContainer>
      )} */}
    </div>
  )
}

export default Withdraw
